/*
  <aoa-fiber>: scroll-linked zoom of the "The fiber" band (sections/aoa-home-fiber.liquid).
  Fallback only: where CSS scroll-driven animations exist, assets/aoa-home-fiber.css runs the
  effect and this element does nothing. Elsewhere it reproduces the same values with
  transform and opacity only:
  - An IntersectionObserver turns a passive scroll/resize listener on while the band is on
    screen and off when it leaves; the work runs once per frame in requestAnimationFrame.
  - Values are read from the section's CSS custom properties, so CSS stays the single source.
  - data-aoa-fiber-active shows the corner pieces that round the media while it zooms.
  - Nothing runs with prefers-reduced-motion (followed live), without data-aoa-fiber-zoom or
    without IntersectionObserver: the final state rendered by the CSS stays visible.
*/
if (!customElements.get('aoa-fiber')) {
  const supportsScrollTimeline =
    !!(window.CSS && CSS.supports) && CSS.supports('(animation-timeline: view()) and (animation-range: entry)');
  const FADE_END = 2 / 3; // same point as the 66.667% keyframe of aoa-home-fiber-fade
  const clamp = (value) => Math.min(1, Math.max(0, value));

  class AoaFiber extends HTMLElement {
    constructor() {
      super();
      this.frame = 0;
      this.listening = null;
      this.items = [];
    }

    connectedCallback() {
      if (supportsScrollTimeline || this.controller || !this.hasAttribute('data-aoa-fiber-zoom')) return;
      if (!('IntersectionObserver' in window)) return;

      this.media = this.querySelector('[data-aoa-fiber-media]');
      this.text = this.querySelector('[data-aoa-fiber-text]');
      if (!this.media || !this.text) return;
      this.corners = Array.from(this.querySelectorAll('.aoa-home-fiber__corner'));

      this.controller = new AbortController();
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', this.toggle, { signal: this.controller.signal });
      this.observer = new IntersectionObserver(this.onIntersect);
      this.toggle();
    }

    disconnectedCallback() {
      this.stopListening();
      if (this.observer) this.observer.disconnect();
      if (this.controller) this.controller.abort();
      this.observer = null;
      this.controller = null;
    }

    toggle = () => {
      this.observer.disconnect();
      this.stopListening();
      if (this.reducedMotion.matches) {
        this.clear();
        return;
      }
      this.setAttribute('data-aoa-fiber-active', '');
      this.measure();
      this.update();
      this.observer.observe(this);
    };

    // Listen to scroll only while the band is on screen; settle the clamped state on exit
    onIntersect = (entries) => {
      const entry = entries[entries.length - 1];
      if (entry.isIntersecting) {
        this.measure();
        this.startListening();
      } else {
        this.stopListening();
      }
      this.requestUpdate();
    };

    onResize = () => {
      this.measure();
      this.requestUpdate();
    };

    requestUpdate = () => {
      if (!this.frame) this.frame = requestAnimationFrame(this.update);
    };

    startListening() {
      if (this.listening) return;
      this.listening = new AbortController();
      const passive = { passive: true, signal: this.listening.signal };
      window.addEventListener('scroll', this.requestUpdate, passive);
      window.addEventListener('resize', this.onResize, passive);
    }

    stopListening() {
      if (this.listening) this.listening.abort();
      this.listening = null;
      cancelAnimationFrame(this.frame);
      this.frame = 0;
    }

    // Motion values from assets/aoa-home-fiber.css (ranges in vh; travel changes at 768px)
    measure() {
      const style = getComputedStyle(this);
      this.scaleFrom = parseFloat(style.getPropertyValue('--aoa-fiber-scale-from')) || 1;
      this.zoomRange = (parseFloat(style.getPropertyValue('--aoa-fiber-zoom-range')) || 100) / 100;
      this.textRange = (parseFloat(style.getPropertyValue('--aoa-fiber-text-range')) || 100) / 100;
      this.items = Array.from(this.text.children).map((element) => ({
        element,
        travel: parseFloat(getComputedStyle(element).getPropertyValue('--aoa-fiber-travel')) || 0,
      }));
    }

    update = () => {
      this.frame = 0;
      // Read both positions first, then write (transforms do not change layout)
      const viewport = window.innerHeight || 1;
      const stageTop = this.getBoundingClientRect().top;
      const textTop = this.text.getBoundingClientRect().top;
      const zoom = clamp((viewport - stageTop) / (viewport * this.zoomRange));
      const rise = clamp((viewport - textTop) / (viewport * this.textRange));
      const eased = 1 - (1 - rise) * (1 - rise);
      const opacity = String(Math.min(1, rise / FADE_END));

      this.media.style.transform = 'scale(' + (this.scaleFrom + (1 - this.scaleFrom) * zoom).toFixed(4) + ')';
      const corner = 'scale(' + (1 - zoom).toFixed(4) + ')';
      this.corners.forEach((element) => {
        element.style.transform = corner;
      });
      this.items.forEach(({ element, travel }) => {
        element.style.transform = 'translateY(' + ((1 - eased) * travel).toFixed(1) + 'px)';
        element.style.opacity = opacity;
      });
    };

    clear() {
      this.removeAttribute('data-aoa-fiber-active');
      this.media.style.removeProperty('transform');
      this.corners.forEach((element) => element.style.removeProperty('transform'));
      Array.from(this.text.children).forEach((element) => {
        element.style.removeProperty('transform');
        element.style.removeProperty('opacity');
      });
    }
  }

  customElements.define('aoa-fiber', AoaFiber);
}
